"use client";

import React from "react";
import { motion } from "framer-motion";
import { Snowflake, Flame, Wrench, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const categories = [{
  title: "Cooling",
  description: "AC & Heat Pumps",
  icon: Snowflake,
  features: ["Diagnostic testing", "Refrigerant recharging", "Compressor repair"]
}, {
  title: "Heating",
  description: "Furnaces & Heat Pumps",
  icon: Flame,
  features: ["Annual tune-ups", "Blower motor repair", "Ignition service"]
}, {
  title: "Installation",
  description: "System Upgrades",
  icon: Wrench,
  features: ["High-efficiency systems", "Smart thermostats", "Upfront estimates"]
}];
const pricingData = [{
  service: "Seasonal Tune-Up",
  price: "Quote from your pro"
}, {
  service: "System Diagnostic",
  price: "Quote from your pro"
}, {
  service: "Urgent AC Service",
  price: "Quote from your pro"
}, {
  service: "Refrigerant Recharge",
  price: "Quote from your pro"
}, {
  service: "Compressor Replacement",
  price: "Quote from your pro"
}, {
  service: "Full System Install",
  price: "Quote from your pro"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServicePricingTable() {
  return <section id="servicepricingtable" className="bg-background py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-4">
            Transparent Pricing
          </Badge>
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            HVAC Service & Installation
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Your pro gives a quote before work starts, ensuring you know what to expect for your home's climate control needs.
          </p>
          <h3 className="text-xl md:text-2xl font-default font-semibold text-foreground">
            Our Service Categories
          </h3>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {categories.map((category, i) => {
          const Icon = category.icon;
          return <motion.div key={i} data-index={i} variants={itemVariants}>
                <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <CardHeader>
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-default">{category.title}</CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {category.features.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="max-w-4xl mx-auto">
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="p-6 border-b border-border bg-muted/30">
              <h3 className="text-xl font-default font-semibold text-foreground">Service Rates</h3>
              <p className="text-sm text-muted-foreground mt-1">Rates and quotes provided directly by local HVAC pros in Atlanta.</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[60%] font-default font-semibold text-foreground py-4">Service</TableHead>
                  <TableHead className="text-right font-default font-semibold text-foreground py-4">Pricing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingData.map((item, i) => <TableRow key={i} data-index={i}>
                    <TableCell className="font-medium py-4">{item.service}</TableCell>
                    <TableCell className="text-right text-muted-foreground py-4">{item.price}</TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
            <div className="p-4 bg-muted/30 border-t border-border text-center text-sm text-muted-foreground">
              Your pro gives a quote before work starts. Warranty terms are set by the pro - confirm before work begins.
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            Looking for more specialized work? Explore our other offerings including <Link to="/PlumbingServicesPage" className="text-primary underline hover:text-primary/80 mx-1">Plumbing Services</Link>, <Link to="/ElectricalServicesPage" className="text-primary underline hover:text-primary/80 mx-1">Electrical Services</Link>, and <Link to="/HomeMaintenancePage" className="text-primary underline hover:text-primary/80 mx-1">Home Maintenance</Link>.
          </div>

          <div className="mt-10 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="#bookingrequest">
                Request a Quote
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground">
              Or call <a href="tel:+14708479076" className="font-medium text-foreground underline hover:text-primary">(470) 847-9076</a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}